// Uptick v0.2 field-mapping panel (uptick_plan.md §13.4). Each entry maps
// one Uptick PromptQuestion to a value TEMPLATE — JSON whose string leaves
// may contain {placeholders} substituted from the test at push time. The
// template approach exists because answer-value shapes are question-type-
// dependent and undocumented (observed ASSERTION: {"result":"F","reason":…}).

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { uptickPushApi, type UptickMappingEntry } from "../lib/api.ts";

const PLACEHOLDERS =
  "{verdict} P/F · {verdict_label} · {test_name} · {date} · {duration_min} · " +
  "{delivered_ah} · {battery_maker} · {battery_model} · {url}";

export function UptickMappingPanel(props: {
  orgId: string;
  initialEntries: UptickMappingEntry[];
}) {
  const qc = useQueryClient();
  const [entries, setEntries] = useState<Array<{ question_id: string; template: string }>>(
    props.initialEntries.length > 0
      ? props.initialEntries.map((e) => ({
          question_id: String(e.question_id),
          template: JSON.stringify(e.template),
        }))
      : [{ question_id: "", template: '{"result":"{verdict}","reason":"BT-1851 {test_name} — {url}"}' }],
  );
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const questions = useQuery({
    queryKey: ["org", props.orgId, "uptick-questions"],
    queryFn: () => uptickPushApi.questions(props.orgId),
    staleTime: 10 * 60_000,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const parsed: UptickMappingEntry[] = entries
        .filter((e) => e.question_id.trim() !== "")
        .map((e) => {
          let template: unknown;
          try { template = JSON.parse(e.template); }
          catch { throw new Error(`Template for question ${e.question_id} is not valid JSON`); }
          return { question_id: Number(e.question_id), template };
        });
      if (parsed.length === 0) throw new Error("Add at least one mapping entry");
      return uptickPushApi.saveMapping(props.orgId, parsed);
    },
    onSuccess: (r) => {
      setMsg({ ok: `Saved ${r.entries} mapping entr${r.entries === 1 ? "y" : "ies"}` });
      qc.invalidateQueries({ queryKey: ["org", props.orgId, "uptick"] });
    },
    onError: (e) => setMsg({ err: (e as Error).message }),
  });

  const qlist = questions.data?.questions ?? [];

  return (
    <div className="card" style={{ maxWidth: "44em", marginBottom: "1.5em" }}>
      <h4 style={{ marginTop: 0 }}>Field mapping</h4>
      <p className="muted" style={{ fontSize: "0.9em" }}>
        Which inspection-form questions receive the test results. Pick the
        question by number (search below), and give the answer value as JSON —
        placeholders: {PLACEHOLDERS}.
      </p>
      {questions.isError && (
        <p className="err">Could not load the tenant&rsquo;s questions: {(questions.error as Error).message}</p>
      )}
      <datalist id="uptick-questions">
        {qlist.map((q) => (
          <option key={q.id} value={String(q.id)}>{`#${q.id} [${q.type}] ${q.label}`}</option>
        ))}
      </datalist>
      {entries.map((e, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "8em 1fr auto", gap: "0.5em", marginBottom: "0.5em" }}>
          <input
            list="uptick-questions"
            placeholder="Question id"
            value={e.question_id}
            onChange={(ev) => setEntries((xs) => xs.map((x, j) => (j === i ? { ...x, question_id: ev.target.value } : x)))}
          />
          <input
            placeholder='{"result":"{verdict}"}'
            value={e.template}
            style={{ fontFamily: "monospace" }}
            onChange={(ev) => setEntries((xs) => xs.map((x, j) => (j === i ? { ...x, template: ev.target.value } : x)))}
          />
          <button type="button" onClick={() => setEntries((xs) => xs.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      {(() => {
        const sel = entries.map((e) => qlist.find((q) => String(q.id) === e.question_id)?.label).filter(Boolean);
        return sel.length > 0 ? (
          <p className="muted" style={{ fontSize: "0.85em" }}>Mapped: {sel.join(" · ")}</p>
        ) : null;
      })()}
      <div style={{ display: "flex", gap: "0.6em" }}>
        <button type="button" onClick={() => setEntries((xs) => [...xs, { question_id: "", template: '{"result":"{verdict}"}' }])}>
          Add field
        </button>
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save mapping"}
        </button>
      </div>
      {msg?.ok && <p className="muted">{msg.ok}</p>}
      {msg?.err && <p className="err">{msg.err}</p>}
    </div>
  );
}
